import { action, get } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type { Planbook } from "@shared/models/planbook"
import { handleError } from "cc-frontend/error-handling"
import FindDoc from "cc-frontend/helpers/find-doc"
import FindManyAltogether from "cc-frontend/helpers/find-many-altogether"
import { hasCapability } from "cc-frontend/helpers/has-capability"
import type { GoogleClassroomCardEditor } from "cc-frontend/lib/google-classroom/card-editor"
import type PlanbookEditor from "cc-frontend/services/planbook-editor"
import type Rpc from "cc-frontend/services/rpc"
import { use } from "ember-could-get-used-to-this"
import { capitalize, compact, find, isNil, lowerCase } from "lodash-es"
import * as R from "remeda"
import { tracked } from "tracked-built-ins"
import styles from "./google-classroom.module.css"

export interface GoogleClassroomModalComponentSignature {
  Args: {
    order: number
    closeModal: () => void
    model: {
      /**
       * Where the edits go: legacy card stacks pass a LegacyGoogleClassroomCardEditor
       * (lesson-editor methods); unified card stacks pass a
       * UnifiedGoogleClassroomCardEditor (updates the cardGoogleClassroom node).
       */
      cardEditor: GoogleClassroomCardEditor
      dateString: string
      cardStackId: string
      planbook: Planbook
    }
  }
}

export default class GoogleClassroomModalComponent extends Component<GoogleClassroomModalComponentSignature> {
  readonly styles = styles

  /* Service */
  @service declare planbookEditor: PlanbookEditor
  @service declare rpc: Rpc

  /* Tracked Properties */
  @tracked canShowTimeZonePicker: boolean = false
  @tracked selectedTimeZone: string | undefined = this.chosenUserTimeZone
  @tracked _selectedModalPane: "SPINNER" | "MAIN" | "SETTINGS" = "MAIN"

  @use user = new FindDoc(() => ["user", this.args.model.planbook.relationships.owner.data.id])
  @use trials = new FindManyAltogether(() => [
    "trial",
    this.user.content?.attributes.activeTrialIds || null,
  ])

  /** The live card, from the passed-in editor (which knows where the card lives). */
  get card() {
    return this.args.model.cardEditor.card
  }

  get googleClassroomTrial() {
    return find(compact(this.trials.content), (trial) => {
      return trial.attributes.trialDescriptionId == "GOOGLE_CLASSROOM"
    })
  }

  get trialCardsLeft() {
    let trial = this.googleClassroomTrial
    if (
      isNil(trial) ||
      isNil(trial.attributes.expiresAtUsage) ||
      isNil(trial.attributes.currentUsage)
    )
      return "..."
    return trial.attributes.expiresAtUsage - trial.attributes.currentUsage
  }

  get selectedModalPane() {
    if (isNil(this.args?.model?.planbook)) return "SPINNER"
    if (this.hasNecessaryScope !== true) {
      return "SETTINGS"
    }
    return this._selectedModalPane
  }

  get hasGoogleClassroomTrial() {
    return hasCapability([this.user.content, "can-use-google-classroom-unlimited"]) === false
  }

  @action goToSettings() {
    this._selectedModalPane = "SETTINGS"
  }

  @action goToMain() {
    this._selectedModalPane = "MAIN"
  }

  get humanizedType() {
    if (this.card && this.card.attributes.postType) {
      // Implementation from
      // https://stackoverflow.com/a/66859069/247678
      return R.pipe(
        this.card.attributes.postType,
        (type) => lowerCase(type),
        (type) => type.split(" "),
        R.map((word) => capitalize(word)),
        (array) => array.join(" ")
      )
    } else {
      return "Assignment"
    }
  }

  @tracked authIsFetched = false
  @tracked cachedAuth: null | { attributes: { name: string; email: string } } = null

  get authForIntegration(): { attributes: { name: string; email: string } } | null {
    if (this.authIsFetched === false && this.googleClassroomIntegration) {
      this.rpc
        .google_classroom_fetch_auth_for_integration({ planbookId: this.args.model.planbook.id })
        .then((response) => {
          if (response.responseOk) {
            this.cachedAuth = response.result.auth.data
            this.authIsFetched = true
          } else {
            console.log("Error", response)
          }
        })
        .catch((error) => {
          console.error("You don't have any GC classes/we couldn't find them", error)
        })
    }
    // @ts-ignore setting this to the typed tracked array is tricky
    return this.cachedAuth
  }

  get chosenUserTimeZone() {
    let gcAuth = this.googleClassroomIntegration
    return gcAuth && get(gcAuth, "timeZoneToPostIn")
  }

  get googleClassroomIntegration() {
    // We have to use the gets because this is a glimmer component
    // and the planbook isn't tracked. If the store becomes tracked
    // or Ember.get has better type support, this can go away
    let content = this.args.model.planbook
    let attributes = get(content, "attributes")
    let integrations = get(attributes, "integrations")
    let googleClassroom = get(integrations || {}, "googleClassroom")
    return googleClassroom
  }

  get hasNecessaryScope() {
    let gcAuth = this.googleClassroomIntegration
    return gcAuth && get(gcAuth, "hasNecessaryScopes")
  }

  get hasChosenTimeZone() {
    let gcAuth = this.googleClassroomIntegration
    return gcAuth && get(gcAuth, "timeZoneToPostIn")
  }

  @action
  toggleTimeZonePicker() {
    this.canShowTimeZonePicker = !this.canShowTimeZonePicker
  }

  @action
  selectZone(timeZone: string): void {
    this.selectedTimeZone = timeZone
  }

  @action
  disconnectGoogleAccount() {
    swal({
      title: "Disconnect Google?",
      text: "Are you sure you want to disconnect from this google account?",
      type: "warning",
      showCancelButton: true,
    })
      .then(() => {
        this.rpc.planbook_remove_integration({
          planbookId: this.args.model.planbook.id,
          integrationName: "googleClassroom",
        })
      })
      .catch((error) => {
        if (error instanceof Error) handleError(error)
      })
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Planner::Modals::GoogleClassroom": typeof GoogleClassroomModalComponent
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Planner::Modals::GoogleClassroom": typeof GoogleClassroomModalComponent
  }
}
